import { FC, useEffect, useMemo, useRef } from 'react';
import { Box } from '@chakra-ui/react';
import ReactMarkdown from 'react-markdown';
import rehypeRaw from 'rehype-raw';
import remarkGfm from 'remark-gfm';
import { CTA_ROOT_ATTRIBUTE, rehypeDropLegacyAds, rehypeInsertCtaSlot } from './articleBodyPlugins';

const TWITTER_WIDGETS_SRC = 'https://platform.twitter.com/widgets.js';

/** Marcadores de un embed de Twitter/X en el cuerpo del articulo. */
const TWITTER_EMBED_PATTERN = /twitter-tweet|(?:twitter|x)\.com\/[^/\s"']+\/status/i;

/** Ancho al que el resizer de Cloudflare sirve las imagenes del cuerpo. */
const BODY_IMAGE_WIDTH = 1200;

const CDN_HOSTS = {
  production: 'https://olagg.io',
  staging: 'https://staging.olagg.io',
  development: 'https://dev.olagg.io',
};

function cdnHost() {
  const env = (import.meta as any).env || {};

  if (env.VITE_APP_ENV === 'production') return CDN_HOSTS.production;
  if (env.VITE_APP_ENV === 'staging') return CDN_HOSTS.staging;

  return CDN_HOSTS.development;
}

/**
 * Sirve la imagen por el resizer de Cloudflare con un ancho fijo. Es lo unico
 * que se puede hacer desde el renderer: el markdown del CMS casi nunca declara
 * dimensiones (67 imagenes de 3.673 articulos), asi que no hay de donde
 * sacarlas.
 */
function resizedSrc(src?: string) {
  const env = (import.meta as any).env || {};

  if (!src) return src;
  if (env.VITE_BYPASS_CLOUDFLARE === 'true') return src;
  if (!/^https?:\/\//.test(src)) return src;
  if (src.includes('/cdn-cgi/image/')) return src;

  const quality = env.VITE_CDN_QUALITY || 70;

  return `${cdnHost()}/cdn-cgi/image/quality=${quality},width=${BODY_IMAGE_WIDTH}/${src}`;
}

/**
 * Atributo con el que se marca el script propio.
 *
 * No se puede buscar por `src`: el snippet que el CMS pega en el cuerpo trae su
 * propio `<script src="platform.twitter.com/widgets.js">`, y React lo inserta
 * como markup, asi que queda en el DOM pero nunca se ejecuta. Un guard por
 * `src` matchea ese script inerte, sale por el early return y widgets.js no se
 * carga nunca -- los embeds quedan como un blockquote sin resolver.
 */
const TWITTER_SCRIPT_MARKER = 'data-olagg-twitter-widgets';

function loadTwitterWidgetScript() {
  const alreadyInjected = document.querySelector(`script[${TWITTER_SCRIPT_MARKER}]`);

  // Una sola carga por sesion. Si ya esta, se le pide a la libreria que
  // resuelva los embeds del cuerpo actual: al navegar entre articulos el
  // script no se vuelve a bajar, pero los blockquotes son nuevos.
  if (alreadyInjected) {
    (window as any).twttr?.widgets?.load?.();
    return;
  }

  const scriptTag = document.createElement('script');
  scriptTag.src = TWITTER_WIDGETS_SRC;
  scriptTag.async = true;
  scriptTag.setAttribute(TWITTER_SCRIPT_MARKER, 'true');
  document.body.appendChild(scriptTag);
}

/**
 * OlaggMarkdown
 * 
 * Implements a markdown component, using the OlaGG styles, fonts and colors
 * 
 * @param {string} baseFontSize Optional - the base font size for the markdown, default is 1rem
 * @param {string} children - the string with markdown to render
 * 
 * @example
 * 
 * import OlaggMarkdown from '@olagg/ui-kit/OlaggMarkdown'
 * 
 * const textWithMarkdown = 'This is a text with **Markdown**: \n # h1 Heading \n ## h2 Heading \n ### h3 Heading';
 *  
 * <OlaggMarkdown
 *   baseFontSize='1.2rem'
 * >
 *   {textWithMarkdown}
 * </OlaggMarkdown>
 * 
 */

interface IOlaggMarkdownProps {
    baseFontSize?: string
    color?: string
    textAlign?: string
    /**
     * Mapping de componentes que se pasa a ReactMarkdown. Lo usa la pagina de
     * articulo para interceptar los `div` con `data-olagg-cta` y renderizar el
     * componente de CTA en su lugar (R7).
     */
    components?: Record<string, any>
    /**
     * Descarta los ads propios del cuerpo (R10). Es un interruptor: en false el
     * cuerpo se renderiza como hasta ahora.
     */
    sanitizeLegacyAds?: boolean
    /**
     * Slot de CTA a marcar dentro del cuerpo, en la posicion que calcula el
     * codigo (30% del texto acumulado). Sin esto no se inyecta nada.
     */
    ctaSlot?: string | null
    /**
     * Relacion de aspecto de reserva para las imagenes que no declaran
     * dimensiones. Se aplica como `aspect-ratio: auto <ratio>`: reserva el
     * espacio mientras la imagen no cargo y cede a la relacion real apenas se
     * conoce, asi que evita el CLS sin deformar ni recortar nada.
     */
    imageAspectRatio?: string
    children: string // children MUST be a string
};

const OlaggMarkdown: FC<IOlaggMarkdownProps> = ({
    baseFontSize = '1rem',
    color,
    textAlign,
    imageAspectRatio = '16 / 9',
    components,
    sanitizeLegacyAds = false,
    ctaSlot = null,
    children
}) => {
    const hasTwitterEmbed = useMemo(
        () => TWITTER_EMBED_PATTERN.test(children || ''),
        [children]
    );

    // Load Twitter script after page loads for rendering tweet styles
    // (TODO: evaluate using `loader` from react-router-dom to preload the script)
    //
    // Solo si el cuerpo trae un embed: se pagaba en el 100% de los articulos
    // para servir al 5,7% que tiene tweets.
    //
    // Depende de `children` y no solo de `hasTwitterEmbed`: al navegar de un
    // articulo con tweets a otro, la bandera no cambia pero los embeds si, y
    // hay que pedirle a la libreria que resuelva los nuevos.
    useEffect(() => {
        if (!hasTwitterEmbed) return;

        loadTwitterWidgetScript();
    }, [hasTwitterEmbed, children]);

    // La primera imagen suele ser la del LCP: esa no se difiere.
    const renderedImages = useRef(0);
    renderedImages.current = 0;

    const renderImage = ({ node, src, alt, width, height, style, ...rest }: any) => {
        const index = renderedImages.current;
        renderedImages.current += 1;

        const hasDeclaredSize = Boolean(width && height);

        return (
            <img
                {...rest}
                src={resizedSrc(src)}
                // alt explicito: sin el, el lector de pantalla lee la URL.
                alt={alt || ''}
                {...(hasDeclaredSize ? { width, height } : {})}
                loading={index === 0 ? 'eager' : 'lazy'}
                decoding={index === 0 ? 'auto' : 'async'}
                {...(index === 0 ? { fetchpriority: 'high' } : {})}
                style={{
                    maxWidth: '100%',
                    height: 'auto',
                    ...(hasDeclaredSize ? {} : { aspectRatio: `auto ${imageAspectRatio}` }),
                    ...style,
                }}
            />
        );
    };

    const customLinkTarget = (url: string, text: string, title: string) => {
        // Open external links in a new tab, internal links in the same tab
        if (url.startsWith('olagg.io') || url.startsWith('http://olagg.io') || url.startsWith('https://olagg.io')) {
            return '_self';
        }
        return '_blank';
    };

    return (
        <Box
            width='100%'
            fontSize={baseFontSize}
            color={color}
            textAlign={textAlign}
            sx={{
                '& h1': {
                    fontSize: '2em',
                    marginBottom: '1.375em'
                },
                '& h2': {
                    fontSize: '1.688em',
                    marginBottom: '1.375em'
                },
                '& h3': {
                    fontSize: '1.438em',
                    marginBottom: '1.375em'
                },
                '& p, li': {
                    fontSize: '1.125em',
                    marginBottom: '1.125em'
                },
                '& li': {
                    listStylePosition: 'inside',
                    marginBottom: '0.625em'
                },
                '& li p': {
                    display: 'inline',
                },
                '& a': {
                    color: '#FEAD10'
                },
                '& img': {
                    margin: '1.875em auto',
                },
                /**
                 * El CTA que se monta dentro del cuerpo NO es contenido del
                 * articulo y no hereda su tipografia. Sin esto, el arte queda
                 * flotando dentro de la tarjeta con una banda de color alrededor
                 * (el margen de las imagenes del cuerpo) y el titulo, el
                 * subtitulo y el boton quedan separados por el margen de los
                 * parrafos.
                 */
                [`& [${CTA_ROOT_ATTRIBUTE}] img`]: {
                    margin: 0,
                },
                [`& [${CTA_ROOT_ATTRIBUTE}] p`]: {
                    fontSize: 'inherit',
                    marginBottom: 0,
                },
                [`& [${CTA_ROOT_ATTRIBUTE}] li`]: {
                    fontSize: 'inherit',
                    marginBottom: 0,
                },
                '& iframe': {
                    width: '100%'
                },
            }}
        >
            <ReactMarkdown
                // El orden importa: `rehypeRaw` parsea el HTML crudo del cuerpo,
                // asi que los ads que vienen como HTML (el dialecto de los
                // editores humanos) solo existen como nodos despues de el.
                rehypePlugins={[
                    rehypeRaw,
                    [rehypeDropLegacyAds, { enabled: sanitizeLegacyAds }],
                    [rehypeInsertCtaSlot, { slot: ctaSlot }],
                ] as any}
                remarkPlugins={[remarkGfm]}
                linkTarget={customLinkTarget as any}
                components={{ img: renderImage, ...components }}
            >
                {children || ''}
            </ReactMarkdown>
        </Box>
    )
}

export default OlaggMarkdown;
